import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Typography, useTheme } from '@mui/material';
import { OptionsObject, useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';

import { API_PATHS } from '../../api/API_App';
import { setLoading } from '../../redux/commonReducer';
import { useAppDispatch } from '../../redux/store';
import { useConfirmDialog } from '../components/ConfirmDialogProvider';
import { useMessageDialog } from '../components/MessageDialogProvider';
import { KEYS_QUERY, some } from '../constants';
import fetchThunk from '../fetchThunk';
import useHookQuery from './useHookQuery';

interface OptionsSnackbarObject extends OptionsObject {
  message?: React.ReactNode;
  requestId?: number;
}
interface Props {
  initialState?: some;
}

const useGeneralHook = (props?: Props) => {
  const { initialState = {} } = props || {};
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { isMobile, isTablet } = useHookQuery();

  const confirmDialog = useConfirmDialog();
  const messageDialog = useMessageDialog();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const intl = useIntl();
  const [state, setState] = useState<some>(initialState);
  const { key, pathname } = useLocation();
  const navigate = useNavigate();

  const setLoadingFnc = useCallback(
    (value: boolean) => {
      dispatch(setLoading(value));
    },
    [dispatch]
  );

  const wrapperEnqueueSnackbar = (params: OptionsSnackbarObject) => {
    const { message, ...rest } = params;
    return enqueueSnackbar(
      <Typography variant="subtitle2" component={'div'} color="inherit">
        {message || 'Error!'}
      </Typography>,
      rest
    );
  };

  const copyToClipboard = (text, noNoti?: boolean) => {
    if (text) {
      navigator.clipboard.writeText(text);
      if (!noNoti)
        wrapperEnqueueSnackbar({
          message: intl.formatMessage({ id: 'copied' }),
          variant: 'success',
        });
    } else {
      if (!noNoti)
        wrapperEnqueueSnackbar({
          message: intl.formatMessage({ id: 'copied_fail' }),
          variant: 'error',
        });
    }
  };

  const navigateTo = (targetUrl: string[]) => {
    if (key === 'default') {
      navigate(targetUrl.join('/'));
      return;
    }

    navigate(-1);
  };

  return {
    intl,
    dispatch,
    fetchThunk,
    API_PATHS,
    enqueueSnackbar: wrapperEnqueueSnackbar,
    closeSnackbar,
    theme,
    confirmDialog,
    messageDialog,
    setLoading: setLoadingFnc,
    state,
    setState: (value) => setState((old) => (value ? { ...old, ...value } : {})),
    isMobile,
    isTablet,
    copyToClipboard,
    KEYS_QUERY: KEYS_QUERY,
    navigateTo,
    pathname,
  };
};

export default useGeneralHook;
